import { Button } from '@prio365/prio365-react-library';
import { useDispatch, useSelector } from 'react-redux';
import {
  getActiveMailFolderId,
  getMessagesIdsState,
  getUserMeContactId,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import {
  ContactId,
  MailFolderId,
  MessageId,
  ProjectId,
} from '../../../../models/Types';
import { useEffect, useMemo, useState } from 'react';
import { assigneeReadMessageProject } from '../../actions/projects/messages';
import { deleteProjectNewsByProjectIdAndType } from '../../../projects/actions';
import { useTranslation } from 'react-i18next';
import { createSelector } from 'reselect';
import { MessageByIdState } from '../../actions/types';

const unreadAssignmentMessagesIdsSelector = (
  projectId: ProjectId,
  mailFolderId: MailFolderId
) =>
  createSelector<
    [
      (state: RootReducerState) => ContactId,
      (state: RootReducerState) => MessageId[],
      (state: RootReducerState) => MessageByIdState,
    ],
    MessageId[]
  >(
    (state) => getUserMeContactId(state),
    (state) => getMessagesIdsState(state, projectId)?.[mailFolderId] || [],
    (state) => state.mail.projects.messages.byId || {},
    (userMeContactId, messageIds, messageById) => {
      return messageIds.filter((id) => {
        const message = messageById[id];
        if (message) {
          return (
            message?.customSingleValueExtendedProperties
              ?.messageAssignmentId === userMeContactId &&
            message?.customSingleValueExtendedProperties
              ?.messageAssignmentCreatedDateTime &&
            !message?.customSingleValueExtendedProperties
              ?.messageAssignmentReadDateTime
          );
        }
        return false;
      });
    }
  );

interface DeleteProjectNewsButtonProps {
  projectId: ProjectId;
}

const DeleteProjectNewsButton = (props: DeleteProjectNewsButtonProps) => {
  //#region ------------------------------ Defaults
  const { projectId } = props;

  const dispatch = useDispatch();

  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const activeMailFolderId = useSelector((state: RootReducerState) =>
    getActiveMailFolderId(state, projectId)
  );
  const unreadAssignmentMessagesIds = useSelector(
    unreadAssignmentMessagesIdsSelector(projectId, activeMailFolderId)
  );
  const hasUnreadAssignmentMessagesIds = useMemo(() => {
    return unreadAssignmentMessagesIds.length > 0;
  }, [unreadAssignmentMessagesIds]);

  const [isMarkingAsRead, setIsMarkingAsRead] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const markMessagesAssignmentsAsRead = () => {
    unreadAssignmentMessagesIds.map((messageId) =>
      dispatch(assigneeReadMessageProject(projectId, messageId))
    );
    dispatch(
      deleteProjectNewsByProjectIdAndType(projectId, [
        'messageAssignmentUnread',
      ])
    );
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (
      unreadAssignmentMessagesIds.length > 0 &&
      isMarkingAsRead &&
      unreadAssignmentMessagesIds.length === 1
    ) {
      setIsMarkingAsRead(false);
    }
  }, [unreadAssignmentMessagesIds, isMarkingAsRead]);
  //#endregion

  if (!hasUnreadAssignmentMessagesIds) {
    return null;
  }

  return (
    <Button
      onClick={
        !isMarkingAsRead
          ? () => {
              setIsMarkingAsRead(true);
              markMessagesAssignmentsAsRead();
            }
          : undefined
      }
      type="default"
      loading={isMarkingAsRead}
      disabled={isMarkingAsRead}
    >
      <span>{t('mail:navigationBar.deleteProjectNews')}</span>
    </Button>
  );
};

export default DeleteProjectNewsButton;
